import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { merge, Subject } from 'rxjs';
import { filter, first, take, takeUntil, tap } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { GdprService } from '@core/gdpr-logic';
import { I18nService } from '@core/i18n-legacy';
import { JobsActions, StatusChangeType } from '@core/offers-data';
import { OfferStatus } from '@core/offers-types';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';
import { injectSlotsLimitDialogOpenerContract } from '@recruitee/billing-contracts';
import { SubscriptionService } from '@recruitee/company-logic';
import { JobsAnalytics } from '@recruitee/offers-legacy';

import type { OfferActionParam, OfferActionsFacade } from './offer-actions-facade.types';

@Injectable()
export class JobActionsFacade implements OfferActionsFacade {
  private readonly slotsLimitDialogOpener = injectSlotsLimitDialogOpenerContract();
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private gdprService: GdprService,
    private router: Router,
    private notificationsCtrl: NotificationsController,
    private i18n: I18nService,
    private dialogService: DialogService,
    private store: Store,
    private actions$: Actions,
    private jobsAnalytics: JobsAnalytics,
    private subscription: SubscriptionService,
  ) {}

  public archive(param: OfferActionParam, onSuccess?: () => void) {
    this.confirmArchive$(param)
      .pipe(
        tap(() => {
          this.archiveFinish(param.id, onSuccess);
          this.jobsAnalytics.trackArchive();
        }),
      )
      .subscribe(() => {
        this.store.dispatch(
          JobsActions.changeStatus({ payload: { id: param.id, status: StatusChangeType.Archive } }),
        );
      });
  }

  public delete(param: OfferActionParam, onSuccess?: () => void) {
    this.confirmDelete$(param.title)
      .pipe(
        tap(() => {
          this.deleteFinish(param.id, onSuccess);
          this.jobsAnalytics.trackDelete();
        }),
      )
      .subscribe(() => {
        this.store.dispatch(JobsActions.removeOne({ payload: param }));
      });
  }

  public duplicate(param: OfferActionParam) {
    this.confirmDuplicate$()
      .pipe(
        tap(() => {
          this.duplicateFinish();
          this.jobsAnalytics.trackDuplicate();
        }),
      )
      .subscribe(() => {
        this.store.dispatch(JobsActions.duplicate({ payload: { id: param.id } }));
      });
  }

  public retrieve(param: OfferActionParam, onSuccess?: () => void) {
    if (!this.subscription.isStatusChangeAllowed(OfferStatus.Archived, OfferStatus.Closed)) {
      this.openSubscriptionUpgradeDialog();
      return;
    }

    this.confirmRetrieve$()
      .pipe(tap(() => this.retrieveFinish(param.id, onSuccess)))
      .subscribe(() => {
        this.store.dispatch(
          JobsActions.changeStatus({
            payload: { id: param.id, status: StatusChangeType.Unarchive },
          }),
        );
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private deleteFinish(jobId: number, onSuccess?: () => void) {
    const success$ = this.actions$.pipe(
      ofType(JobsActions.removeOneSuccess),
      filter(action => action.payload.id === jobId),
      tap(() => {
        this.notificationsCtrl.success(
          this.i18n.translate(`app.offers.actions.delete.success_message.job`),
        );
        if (onSuccess) onSuccess();
      }),
    );
    const failed$ = this.actions$.pipe(
      ofType(JobsActions.removeOneFailed),
      filter(action => action.payload.id === jobId),
    );

    merge(success$, failed$).pipe(take(1)).subscribe();
  }

  private retrieveFinish(jobId: number, onSuccess?: () => void) {
    const success$ = this.actions$.pipe(
      ofType(JobsActions.changeStatusSuccess),
      filter(action => action.payload.offer.id === jobId),
      tap(() => {
        if (onSuccess) onSuccess();
        this.notificationsCtrl.success(
          this.i18n.translate(`app.offers.actions.unarchive.success_message.job`),
        );
      }),
    );
    const failed$ = this.actions$.pipe(
      ofType(JobsActions.changeStatusFailed),
      filter(action => action.payload.id === jobId),
    );

    merge(success$, failed$).pipe(take(1)).subscribe();
  }

  private archiveFinish(jobId: number, onSuccess?: () => void) {
    const failed$ = this.actions$.pipe(
      ofType(JobsActions.changeStatusFailed),
      filter(action => action.payload.id === jobId),
    );
    const success$ = this.actions$.pipe(
      ofType(JobsActions.changeStatusSuccess),
      filter(({ payload: { offer } }) => offer.id === jobId),
      tap(({ payload: { offer } }) => {
        this.notificationsCtrl.success(
          this.i18n.translate(`app.offers.actions.archive.success_message.job`),
        );
        if (onSuccess) onSuccess();
      }),
    );

    merge(success$, failed$).pipe(take(1)).subscribe();
  }

  private duplicateFinish() {
    const success$ = this.actions$.pipe(
      ofType(JobsActions.duplicateSuccess),
      tap(action => this.createDuplicateEffect(action.payload.offer)),
    );
    const failed$ = this.actions$.pipe(ofType(JobsActions.duplicateFailed));
    merge(success$, failed$).pipe(take(1)).subscribe();
  }

  private createDuplicateEffect(job: { slug: string; id: number }) {
    const notificationRef = this.notificationsCtrl.success({
      title: this.i18n.translate('app.offers.actions.duplicate.success_message.job'),
      topButtonLabel: this.i18n.translate(
        'app.offers.actions.duplicate.success_message.button_label',
      ),
    });
    notificationRef
      .topButtonClick()
      .pipe(first(), takeUntil(notificationRef.afterClosed))
      .subscribe(() => {
        this.router.navigate([{ outlets: { 'job-editor': [job.id] } }]);
      });
  }

  private confirmRetrieve$() {
    return this.dialogService
      .information({
        message: this.i18n.translate('app.offers.actions.unarchive.confirm_message.job'),
        title: this.i18n.translate('app.offers.actions.unarchive.confirm_title.job'),
        agreeLabel: this.i18n.translate('app.offers.actions.unarchive.confirm_agree_label.job'),
      })
      .pipe(take(1), filterBoolean);
  }

  private confirmDuplicate$(): Observable<boolean> {
    return this.dialogService
      .warning({
        message: this.i18n.translate('app.offers.actions.duplicate.confirm_message.job'),
        title: this.i18n.translate('app.offers.actions.duplicate.confirm_title.job'),
        agreeLabel: this.i18n.translate('app.offers.actions.duplicate.confirm_agree_label.job'),
      })
      .pipe(take(1), filterBoolean);
  }

  private confirmDelete$(title: string): Observable<boolean> {
    return this.dialogService
      .error({
        message: this.i18n.translate('app.offers.actions.delete_dialog.job.message', {
          jobName: title,
        }),
        title: this.i18n.translate('app.offers.actions.delete_dialog.job.title'),
        agreeLabel: this.i18n.translate('app.offers.actions.delete_dialog.job.agree_label'),
        checkboxes: [
          {
            title: this.i18n.translate(
              'app.offers.actions.delete_dialog.job.checkboxes.checkbox_1',
            ),
          },
        ],
        safeFooter: true,
      })
      .pipe(take(1), filterBoolean);
  }

  private confirmArchive$(param: OfferActionParam): Observable<boolean> {
    const dialogOptions = {
      message: this.i18n.translate('app.offers.actions.archive.confirm_message.job_draft'),
      title: this.i18n.translate('app.offers.actions.archive.confirm_title.job'),
      agreeLabel: this.i18n.translate('app.offers.actions.archive.confirm_agree_label.job'),
    };
    const hasUnlimitedSlots = !this.subscription.hasSlotsLimit();
    const isGdprEnabled = this.gdprService.isGdprEnabled();

    if (param.status === OfferStatus.Draft) {
      dialogOptions.message = this.i18n.translate(
        'app.offers.actions.archive.confirm_message.job_draft',
      );
    } else if (isGdprEnabled && !hasUnlimitedSlots) {
      dialogOptions.message = this.i18n.translate(
        'app.offers.actions.archive.confirm_message.job_gdpr_limited_slots',
      );
    } else if (isGdprEnabled && hasUnlimitedSlots) {
      dialogOptions.message = this.i18n.translate(
        'app.offers.actions.archive.confirm_message.job_gdpr_unlimited_slots',
      );
    } else if (!isGdprEnabled && hasUnlimitedSlots) {
      dialogOptions.message = this.i18n.translate(
        'app.offers.actions.archive.confirm_message.job_no_gdpr_unlimited_slots',
      );
    } else if (!isGdprEnabled && !hasUnlimitedSlots) {
      dialogOptions.message = this.i18n.translate(
        'app.offers.actions.archive.confirm_message.job_no_gdpr_limited_slots',
      );
    }

    return this.dialogService.warning(dialogOptions).pipe(take(1), filterBoolean);
  }

  private openSubscriptionUpgradeDialog(): void {
    this.slotsLimitDialogOpener.open(
      { width: DialogWidth.Small },
      { isTrial: this.subscription.isTrial() },
    );
  }
}
